// Relative, not the `@/` alias: this module is covered by `tests/accessTabs.test.ts`, which runs
// under `node --test` where only the bundler resolves the alias.
import { can, isAdmin, type PermissionAction, type SessionProfile } from '../auth/session.ts';

export type AccessTab = 'users' | 'permissions' | 'scopes' | 'geography';

/**
 * The three questions on the access screen, and the screen permission that answers each.
 *
 * They are three separate grants in the database, not one: `user.list` says who exists,
 * `role.configuration` says what a role may see, `user.geography.scope` says where a person
 * works. The screen has two doors - "Users & devices" gated on `user.list` and "Access & scope"
 * on `role.configuration` - so whichever one a reader came through, they were being shown all
 * three tabs. The tabs are the answer to that: a reader sees the ones they hold.
 *
 * This is not the enforcement. Every row here is read through an api view under RLS and written
 * through a permission-checked policy or function; the point is not to offer an honest reader
 * something they will only be refused.
 *
 * WHAT THE DATABASE ACTUALLY ASKS, read from the policies rather than assumed:
 *   read   `<screen>` read. `authz.fn_has_permission` is purely grant-based - it has NO tier
 *          bypass - so an admin without the grant reads nothing, and must not be shown the tab.
 *          The `tier <= 1` that appears in the geography policy is about WHICH PLACES may be
 *          seen, not about whether the permission is needed.
 *   write  the grant AND `tier <= 1`, because `write_min_tier` is 1 on both writable tables and
 *          the policy ANDs the two. Neither alone is enough.
 */
export const ACCESS_TABS: Array<{ id: AccessTab; label: string; screenKey: string; alsoNeeds?: string }> = [
  { id: 'users', label: 'Users', screenKey: 'user.list' },
  // TWO grants, because the matrix is built from two tables with two different select policies:
  // `authz.role_permissions` asks for `role.configuration` read - what this role currently holds -
  // and `authz.permissions` asks for `screen.permissions` read - which screens and actions exist
  // at all. Hold only the first and every cell with no row would simply be missing, and an
  // ungranted permission would be indistinguishable from one the catalogue does not have. A
  // permissions grid that quietly omits permissions is worse than no grid.
  { id: 'permissions', label: 'Permissions', screenKey: 'role.configuration', alsoNeeds: 'screen.permissions' },
  { id: 'scopes', label: 'Record scope', screenKey: 'role.configuration' },
  { id: 'geography', label: 'Geography scope', screenKey: 'user.geography.scope' },
];

/** The tabs this reader may read. No shortcut for an admin: the select policies give them none. */
export function visibleAccessTabs(profile: SessionProfile | null | undefined) {
  if (!profile) return [];
  return ACCESS_TABS.filter((tab) => can(profile, tab.screenKey, 'read')
    && (!tab.alsoNeeds || can(profile, tab.alsoNeeds, 'read')));
}

/**
 * The tab to show: the one asked for while it is still permitted, otherwise the first that is.
 * Derived rather than stored, so a profile that arrives late - or a permission that changes when
 * an operator switches organisation - cannot leave the screen pointing at a tab it may not read.
 */
export function activeAccessTab(
  profile: SessionProfile | null | undefined,
  wanted: AccessTab | null,
): AccessTab | null {
  const tabs = visibleAccessTabs(profile);
  if (wanted && tabs.some((tab) => tab.id === wanted)) return wanted;
  return tabs[0]?.id ?? null;
}

/**
 * May this reader write here? The grant AND tier 0/1, exactly as the row policy reads:
 *
 *   fn_has_permission('role.configuration', 'create') AND coalesce(fn_current_tier(), 99) <= 1
 *
 * Asking only whether someone is an admin - which is what this screen used to do - offers the
 * button to a tier 1 admin who was never granted the permission, and the write is then refused
 * by the database. Asking only for the grant offers it to a manager the tier floor will stop.
 */
export function canWriteAccess(
  profile: SessionProfile | null | undefined,
  screenKey: string,
  action: PermissionAction,
): boolean {
  return isAdmin(profile) && can(profile, screenKey, action);
}

/**
 * Which grant each write on this screen actually needs.
 *
 * INVITE IS NOT `user.list`. `api.fn_invite_user` asks for `user.onboarding` create, because
 * inviting someone is onboarding rather than reading the directory - a reader may well hold one
 * and not the other.
 */
export const ACCESS_WRITES = {
  invite: { screenKey: 'user.onboarding', action: 'create' },
  addScope: { screenKey: 'role.configuration', action: 'create' },
  editScope: { screenKey: 'role.configuration', action: 'update' },
  assignPlace: { screenKey: 'user.geography.scope', action: 'create' },
  // `api.fn_set_role_permission` asks for `role.configuration` CONFIGURE, not create or update.
  // It is the stricter of the two policies on `authz.role_permissions` - insert wants the grant
  // and the tier, update wants only the tier - taken on purpose so that withdrawing a permission
  // is never easier than granting it.
  setPermission: { screenKey: 'role.configuration', action: 'configure' },
  // `api.fn_set_user_status` asks for `user.list` UPDATE. Suspending somebody is a change to the
  // directory, not an invitation, so it is not `user.onboarding`.
  setUserStatus: { screenKey: 'user.list', action: 'update' },
} as const satisfies Record<string, { screenKey: string; action: PermissionAction }>;

/**
 * May this reader change what `role` may do?
 *
 * The three refusals `api.fn_set_role_permission` raises, asked before the button is offered
 * rather than after the click is refused:
 *   - the permission and tier 0/1 (`canWriteAccess`);
 *   - never a tier 0 role, whoever you are;
 *   - never a role at or above your own level, or a tier 1 admin could widen a role above them
 *     and then be assigned it.
 */
export function canEditRolePermissions(
  profile: SessionProfile | null | undefined,
  role: { tier: number } | null | undefined,
): boolean {
  if (!role || !canWriteAccess(profile, ACCESS_WRITES.setPermission.screenKey, ACCESS_WRITES.setPermission.action)) return false;
  if (role.tier === 0) return false;
  return profile?.tier != null && role.tier > profile.tier;
}

/**
 * Activating a login and clearing a forgotten approval PIN ask for the tier and nothing else -
 * `provision-user` refuses above tier 1, and `api.fn_reset_approval_pin` raises above it. They
 * are deliberately not in `ACCESS_WRITES`: inventing a permission for them here would be the
 * interface asking for something the server does not.
 */
export function canAdministerLogins(profile: SessionProfile | null | undefined): boolean {
  return isAdmin(profile);
}

/**
 * Which roles the person signed in may actually hand out.
 *
 * `api.fn_invite_user` refuses tier 0 from inside an organisation at all, and since migration
 * 0130 refuses tier 1 to anyone who is not tier 0 - administrators are made by the app owner
 * (owner, 2026-09-26: "Keep admin can be added or changed by superadmin alone"). Offering a role
 * the server will refuse is the picker lying about what it can do, so the list is filtered to
 * match rather than left to fail on submit.
 *
 * This is the INVITE list only. Choosing a role to scope a geography onto is a different
 * question and still covers every role in the organisation.
 */
export function grantableRoles<T extends { tier: number }>(
  profile: SessionProfile | null | undefined,
  roles: readonly T[],
): T[] {
  const tier = profile?.tier ?? 99;
  return roles.filter((role) => role.tier > 0 && (role.tier > 1 || tier === 0));
}
